import { useFormState } from 'hooks/useFormState';
import { createContext, useContext, useEffect, useMemo } from 'react';
import styles from './FormSection.module.scss';
interface FormSectionProps {
    title: string;
    children: React.ReactNode;
    condensed?: boolean;
}

/**
 * Registers new form section as a new section in form state pagination
 * @param {string} title - title of the form section to be added
 * @param {string} children - form section content
 * @param {boolean} condensed - remove all margin between form inputs
 * @returns FormSection component
 */
export function FormSection({ title, children, condensed }: FormSectionProps) {
    const { formSections, currentFormSectionIndex, addFormSection } =
        useFormState();

    useEffect(() => {
        if (!formSections.includes(title)) {
            addFormSection(title);
        }
    }, [title, addFormSection, formSections]);

    const showSection = useMemo(() => {
        return currentFormSectionIndex === formSections.indexOf(title);
    }, [currentFormSectionIndex, formSections, title]);

    return (
        <FormSectionContextProvider formSection={title}>
            <div
                hidden={!showSection}
                className={`${styles.formSection} ${
                    condensed ? styles.condensed : ''
                }`}
                data-testid={`${title}FormSection`}
            >
                {children}
            </div>
        </FormSectionContextProvider>
    );
}

type FormSectionContextValue = {
    formSection: string;
};

const FormSectionContext = createContext<FormSectionContextValue | null>(null);

export const useFormSectionContext = () => {
    const context = useContext(FormSectionContext);
    if (!context) {
        return {
            formSection: '',
        };
    }

    return context;
};

interface FormSectionContextProviderProps {
    children: React.ReactNode;
    formSection: string;
}

function FormSectionContextProvider({
    children,
    formSection,
}: FormSectionContextProviderProps) {
    return (
        <FormSectionContext.Provider value={{ formSection }}>
            {children}
        </FormSectionContext.Provider>
    );
}
